<template>
    <div>
        <div class="row" v-if="statsLoaded">
            <div
                class="col-md-12"
                v-for="(item, idx) in stats.questionSummaries"
                :key="idx"
            >
                <div class="card">
                    <div class="card-header pl-3 d-block text-left">
                        <p class="mb-0">
                            <span class="light-text"
                                >{{ __('QUESTION') }} {{ parseInt(item.order) + 1 }}</span
                            >
                        </p>
                        <p class="field-title mb-0">{{ item.title }}</p>
                    </div>
                    <div
                        class="card-body p-3"
                        v-if="item.type == 'explanation'"
                    >
                        <p class="readable-text">
                            Explanation fields don't receive responses.
                        </p>
                    </div>
                    <div
                        class="card-body p-3"
                        v-if="item.type !== 'explanation'"
                        :class="{ 'card-text': item.type == 'text' }"
                    >
                        <div class="metrics row" v-if="item.type == 'text'">
                            <div class="col-sm-4 border-right mb-md-0 mb-3">
                                <div class="mb-2">
                                    <div
                                        class="face face-lite d-inline-block mr-1 success"
                                    ></div>
                                    <strong class="light-text text-uppercase">{{ __('Positive') }}</strong>
                                </div>
                                <p class="metric-number">
                                    {{
                                        textFieldsInfo[idx]
                                            ? textFieldsInfo[idx].totalPositive
                                            : 0
                                    }}
                                </p>
                            </div>
                            <div class="col-sm-4 border-right mb-md-0 mb-3">
                                <div class="mb-2">
                                    <div
                                        class="face face-lite d-inline-block mr-1 danger"
                                    ></div>
                                    <strong class="light-text text-uppercase">{{ __('Negative') }}</strong>
                                </div>
                                <p class="metric-number">
                                    {{
                                        textFieldsInfo[idx]
                                            ? textFieldsInfo[idx].totalNegative
                                            : 0
                                    }}
                                </p>
                            </div>
                            <div class="col-sm-4 border-right mb-md-0 mb-3">
                                <p class="metric-label" style="height:30px;">
                                    <strong>{{ __("Total Responses") }}</strong>
                                </p>
                                <p class="metric-number">
                                    {{ item.totalResponses }}
                                </p>
                            </div>
                        </div>

                        <div class="metrics row">
                            <div
                                class="col-sm-6 border-right"
                                v-if="item.type != 'text'"
                            >
                                <div v-if="item.isNps == 1">
                                    <p class="metric-label">
                                        <strong>{{ __("NPS Score") }}</strong>
                                    </p>
                                    <a
                                        href="#" tabindex="0" role="button"
                                        data-toggle="popover" data-trigger="focus" 
                                        data-placement="bottom" data-html="true" title="NPS Breakdown"
                                        :data-content= "  
                                            'Promoters: ' + item.npsScore.promoters + '<br/>' + 
                                            'Detractors: ' + item.npsScore.detractors + '<br/>' +
                                            'Passives: ' + item.npsScore.passive
                                        "
                                        @click.prevent
                                        class="blue-link nps-metric">
                                        {{ item.npsScore.company }}
                                    </a>
                                </div>
                                <div v-else>
                                    <p class="metric-label">
                                        <strong>{{
                                            __("Overall Score")
                                        }}</strong>
                                    </p>
                                    <p class="metric-number">
                                        {{
                                            item.averageScore
                                                ? (
                                                      item.averageScore /
                                                      (10 / scoreOutOf)
                                                  ).toFixed(2) +
                                                  "/" +
                                                  scoreOutOf
                                                : "N/A"
                                        }}
                                    </p>
                                </div>
                            </div>
                            <div class="col-sm-6 border-right" v-else></div>
                            <div
                                class="col-sm-6 border-right"
                                v-if="item.type != 'text'"
                            >
                                <p class="metric-label">
                                    <strong>{{ __("Total Responses") }}</strong>
                                </p>
                                <p class="metric-number">
                                    {{ item.totalResponses }}
                                </p>
                            </div>
                        </div>
                        <div
                            class="row"
                            v-if="
                                item.type !== 'text' &&
                                    item.type !== 'instructions'
                            "
                        >
                            <div class="col-md-9">
                                <bar-chart
                                    v-if="item.type == 'range'"
                                    :height="170"
                                    :chartdata="getDiagramData(item).data"
                                    :options="getDiagramData(item).options"
                                ></bar-chart>
                                <pie-chart
                                    v-if="
                                        item.type == 'checkbox' ||
                                            item.type == 'radiobutton'
                                    "
                                    :height="170"
                                    ref="pie-chart"
                                    :chartdata="getDiagramData(item).data"
                                    :options="getDiagramData(item).options"
                                ></pie-chart>
                            </div>
                            <div class="col-md-3 pl-2">
                                <table class="border-left table">
                                    <tbody>
                                        <tr
                                            v-for="option in getResultsForDisplay(
                                                item
                                            )"
                                            :key="option.id"
                                        >
                                            <th class="p-0 pr-2" scope="row">
                                                <a
                                                    href="#"
                                                    class="blue-link"
                                                    @click.prevent="
                                                        filterByAnswerOption(
                                                            option,
                                                            item
                                                        )
                                                    "
                                                >
                                                    {{ option.value }}
                                                </a>
                                            </th>
                                            <td class="p-0 pr-2">
                                                {{ option.total }}
                                            </td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                        </div>
                        <div
                            class="row response-comment"
                            v-if="
                                item.type === 'text' &&
                                    textFieldsInfo[idx] != null
                            "
                        >
                            <div
                                class="guest-response mb-3 border-bottom col-md-12 row"
                                v-for="response in textFieldsInfo[idx].data"
                                :key="response.id"
                            >
                                <div class="col-md-3">
                                    <table>
                                        <tr>
                                            <th scope="row">{{ __('Name') }}:</th>
                                            <td>
                                                {{
                                                    response.guest.firstName +
                                                        " " +
                                                        response.guest.lastName
                                                }}
                                            </td>
                                        </tr>
                                        <tr>
                                            <th scope="row">{{ __('Email') }}:</th>
                                            <td>{{ response.guest.email }}</td>
                                        </tr>
                                        <tr>
                                            <th scope="row">{{ __('Posted') }}:</th>
                                            <td>
                                                {{
                                                    formatDate(
                                                        response.createdAt
                                                    )
                                                }}
                                            </td>
                                        </tr>
                                    </table>
                                </div>
                                <div class="col-md-9">
                                    <p
                                        class="readable-text"
                                        v-html="
                                            highlightTerms(
                                                response,
                                                null,
                                                showAllSentiment
                                            )
                                        "
                                    ></p>
                                </div>
                            </div>
                        </div>
                        <div class="pt-3" v-if="item.type == 'range'">
                            <div>
                                <div class="btn-group-toggle d-flex justify-content-between">
                                    <div></div>
                                    <label class="btn btn-primary">
                                        <input
                                            type="checkbox"
                                            :id="item.fieldId"
                                            :value="item.fieldId"
                                            autocomplete="off"
                                            v-model="shownBreakdowns"
                                        />
                                        <i class="fa fa-chart-pie"></i>
                                        Show Breakdown
                                    </label>
                                </div>
                            </div>
                            <survey-stats-breakdown
                                :fromDate="fromDate"
                                :toDate="toDate"
                                :survey="survey"
                                :surveyField="item"
                                class="pt-3"
                                v-if="shownBreakdowns.includes(item.fieldId)"
                            />
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import SurveysService from "../../services/surveys-service";
import HorizontalBarChart from "../charts/horizontal-bar-chart";
import PieChart from "../charts/pie-chart";
import BarChart from "../charts/bar-chart";
import SurveyStatsBreakdown from "./SurveyStatsBreakdown.vue";
export default {
    components: {
        HorizontalBarChart,
        BarChart,
        PieChart,
        SurveyStatsBreakdown
    },
    props: {
        survey: {
            type: Object
        },
        fromDate: {
            type: Object
        },
        toDate: {
            type: Object
        },
        comparisonFromDate: {
            type: Object
        },
        comparisonToDate: {
            type: Object
        },
        answerId: {
            type: Number
        },
        params: {
            type: Object
        }
    },
    computed: {
        scoreOutOf() {
            return this.survey.options.outOfFive === 1 ? 5 : 10;
        }
    },
    created() {
        this.getStats();
    },
    mounted() {
        $(function () {
            $('[data-toggle="popover"]').popover()
        })
    },
    updated() {
        $(function () {
            $('[data-toggle="popover"]').popover()
        })
    },
    data() {
        return {
            statsLoaded: false,
            stats: {},
            textFieldsInfo: {},
            shownBreakdowns: [],
            showAllSentiment: true
        };
    },
    watch: {
        survey: function(newVal, oldVal) {
            if (newVal) {
                this.getStats();
            }
        },
        fromDate: function(newVal, oldVal) {
            if (this.survey) {
                this.getStats();
            }
        },
        answerId: function(newVal, oldVal) {
            this.getStats();
        }
    },
    methods: {
        getResultsForDisplay(item) {
            if (
                this.survey.options.outOfFive === 0 ||
                item.type !== "range" ||
                item.isNps
            ) {
                return item.results;
            }
            return item.results
                .filter(item => {
                    return parseInt(item.value) % 2 == 0;
                })
                .map(item => {
                    if (item.isNps == 1) {
                        return {
                            total: item.total,
                            value: item.value
                        };
                    }
                    return {
                        total: item.total,
                        value: parseInt(item.value) / 2
                    };
                });
        },
        filterByAnswerOption(option, field) {
            this.$emit("option-filtered", option, field);
        },
        formatDate(date) {
            return moment(date).format("D MMM YYYY");
        },
        async getStats() {
            if (!this.survey) {
                return;
            }

            const from = this.fromDate.format("YYYY-MM-DD");
            const to = this.toDate.format("YYYY-MM-DD");

            const data = await SurveysService.surveyStats(
                this.survey.id,
                from,
                to,
                null,
                this.answerId
            );

            this.stats = data;
            this.textFieldsInfo = {};

            if (this.stats) {
                const sentimentData = await SurveysService.sentiment.getOverview(
                    this.survey.id,
                    from,
                    to
                );
                const terms = await SurveysService.sentiment.index(
                    this.survey.id,
                    from,
                    to
                );

                await Promise.all(
                    Object.keys(this.stats.questionSummaries).map(
                        async field => {
                            let fieldId = this.stats.questionSummaries[field]
                                .fieldId;

                            if (
                                this.stats.questionSummaries[field].type ===
                                "text"
                            ) {
                                const responses = await SurveysService.responses.index(
                                    1,
                                    {
                                        fieldId: this.stats.questionSummaries[
                                            field
                                        ].fieldId,
                                        answerId: this.answerId,
                                        from: from,
                                        to: to
                                    }
                                );

                                Object.keys(terms).map(function(responseId) {
                                    let entry = responses.data.find(
                                        entry => entry.id == responseId
                                    );

                                    if (!entry) {
                                        return;
                                    }

                                    entry.terms = terms[responseId][fieldId];
                                });

                                responses.totalPositive =
                                    sentimentData.terms.totals.positive[
                                        field
                                    ] || 0;
                                responses.totalNegative =
                                    sentimentData.terms.totals.negative[
                                        field
                                    ] || 0;
                                Vue.set(this.textFieldsInfo, field, responses);
                            }
                        }
                    )
                );
            }

            this.statsLoaded = true;
        },
        getDiagramData(item) {
            var bg = "#00aef1";
            if (item.type == "radiobutton" || item.type == "checkbox") {
                bg = ["#00afef", "#8b72f5", "#0153b9", "#44d4d2"];
            }
            const vm = this;
            const data = {
                labels: item.results
                    .filter(x => {
                        if (
                            vm.survey.options.outOfFive === 0 ||
                            item.type !== "range" ||
                            item.isNps === 1
                        ) {
                            return true;
                        }
                        return x.value % 2 == 0;
                    })
                    .map(x => {
                        if (
                            vm.survey.options.outOfFive === 0 ||
                            item.type !== "range" ||
                            item.isNps === 1
                        ) {
                            return x.value;
                        }
                        return parseInt(x.value) / 2;
                    }),
                datasets: [
                    {
                        backgroundColor: bg,
                        data: item.results
                            .filter(x => {
                                if (
                                    vm.survey.options.outOfFive === 0 ||
                                    item.type !== "range" ||
                                    item.isNps === 1
                                ) {
                                    return true;
                                }
                                return x.value % 2 == 0;
                            })
                            .map(x => x.total)
                    }
                ]
            };

            const options = {
                plugins: {
                    datalabels: false
                },
                scales: {
                    xAxes: [
                        {
                            gridLines: {
                                display: false
                            },
                            ticks: {
                                min: 0,
                                stepSize: 5, // minimum will be 0, unless there is a lower value.
                                // OR //
                                beginAtZero: true,
                                callback: function(label, index, labels) {
                                    return label;
                                } // minimum value will be 0.
                            }
                        }
                    ],
                    yAxes: [
                        {
                            gridLines: {
                                display: false
                            }
                        }
                    ]
                },
                legend: {
                    display: false
                }
            };
            return { data, options };
        },
        highlightTerms(comment, language, showSentiment) {
            if (!comment) {
                return;
            }

            let txt = comment.answer;

            if (!comment.terms) {
                return txt;
            }

            let should_highlight = comment.terms.length > 0;

            let comment_str = "";
            if (should_highlight || showSentiment) {
                let terms = [];

                if (showSentiment) {
                    terms = comment.terms;
                } else {
                    terms = this.filterTermsByParams(
                        comment.terms,
                        this.$parent.params,
                        this.$parent.additionalParams
                    );
                }

                comment_str = this.buildHighlightedCommentFromTerms(terms, txt);
            } else {
                comment_str = txt;
            }

            return comment_str;
        },
        filterTermsByParams(terms, params, additionalParams) {
            // If no filters are active then highlighting is inactive.
            if (
                !params["sentimentCategory"] &&
                !params["keyword"] &&
                !params["terms"] &&
                !params["positiveNegativeOrBoth"] &&
                !params["insightRolling"] &&
                !params["insightAdhoc"]
            ) {
                return [];
            }

            if (params["sentimentCategory"]) {
                terms = terms.filter(function(element) {
                    return element.category == params["sentimentCategory"];
                });
            }

            if (params["positiveNegativeOrBoth"]) {
                if (params["positiveNegativeOrBoth"] == "positive") {
                    terms = terms.filter(function(element) {
                        return element.sentiment_score > 0;
                    });
                } else if (params["positiveNegativeOrBoth"] == "negative") {
                    terms = terms.filter(function(element) {
                        return element.sentiment_score < 0;
                    });
                }
            }

            if (params["keyword"]) {
                terms = terms.filter(function(element) {
                    return element.target == params["keyword"];
                });
            }

            if (params["subcategory"]) {
                terms = terms.filter(function(element) {
                    return element.subcategory == params["subcategory"];
                });
            }

            if (params["terms"].length > 0) {
                let tr = params["terms"].split(":");

                terms = terms.filter(function(element) {
                    return element.target == tr[1] && element.term == tr[0];
                });
            }

            if (params["insightRolling"] || params["insightAdhoc"]) {
                let keyword = additionalParams["insightKeyword"];

                terms = terms.filter(function(element) {
                    return element.target == keyword;
                });
            }

            return terms;
        },
        buildHighlightedCommentFromTerms(terms, text) {
            let highlightedText = "";

            // Replace new lines with spaces
            text = text.replace(new RegExp("\\n", "gm"), " ");

            // If any double+ spaces occur then they are replaced by single spaces
            text = text.replace(/  +/g, " ");

            if (terms) {
                var positions = [];
                var filled_positions = [];

                for (let term of terms) {
                    // Skip neutral terms
                    if (term.sentiment_score == 0) {
                        continue;
                    }

                    let sentiment_type = "neutral";

                    if (term.sentiment_score < 0) {
                        sentiment_type = "negative";
                    } else if (term.sentiment_score > 0) {
                        sentiment_type = "positive";
                    }

                    let term_position = {
                        type: "term",
                        start: term.term_start,
                        end: term.term_end,
                        sentiment_type: sentiment_type,
                        expected_word: term.term,
                        term_id: term.id
                    };

                    let keyword_position = {
                        type: "keyword",
                        start: term.keyword_start,
                        end: term.keyword_end,
                        expected_word: term.target,
                        term_id: term.id
                    };

                    let keyword_str =
                        term.keyword_start + ":" + term.keyword_end;
                    let term_str = term.term_start + ":" + term.term_end;

                    if (!filled_positions.includes(keyword_str)) {
                        filled_positions.push(keyword_str);
                        positions.push(keyword_position);
                    }

                    if (!filled_positions.includes(term_str)) {
                        filled_positions.push(term_str);
                        positions.push(term_position);
                    }
                }
            }

            let current = 0;

            // Sort positions
            positions.sort(function compare(a, b) {
                if (a.start < b.start) return -1;
                if (a.start > b.start) return 1;
                return 0;
            });

            for (let position of positions) {
                let position_current = current;
                let position_start = position.start;
                let position_end = position.end;
                let expected_word = position.expected_word;
                let highlightedWord = text.substring(
                    position_start,
                    position_end
                );
                let steps = -2;
                if (
                    highlightedWord.toLowerCase() != expected_word.toLowerCase()
                ) {
                    for (let i = 0; i < 50; i++) {
                        let start = position_start + (steps + i);
                        let end = position_end + (steps + i);
                        if (
                            text.substring(start, end).toLowerCase() ==
                            expected_word.toLowerCase()
                        ) {
                            position_start = start;
                            position_end = end;
                            highlightedWord = text.substring(
                                position_start,
                                position_end
                            );
                            break;
                        }
                    }
                }
                if (
                    highlightedWord.toLowerCase().substring(0, 4) !=
                    expected_word.toLowerCase().substring(0, 4)
                ) {
                    continue;
                }

                highlightedText += text.substring(current, position_start);
                let className =
                    position.type == "term" ? "term-mark" : "keyword-mark";

                // Add term sentiment
                if (position.type == "term") {
                    className += " " + position.sentiment_type;
                }

                let randomString = Math.random()
                    .toString(36)
                    .substr(7);

                if (position.type === "term") {
                    highlightedText +=
                        '<a data-term-id="' +
                        position.term_id +
                        '" tabindex="0" role="button" id="' +
                        randomString +
                        '" class="' +
                        className +
                        '" data-toggle="popover">' +
                        highlightedWord +
                        "</a>";
                } else {
                    highlightedText +=
                        '<span class="' +
                        className +
                        '">' +
                        highlightedWord +
                        "</span>";
                }
                current = position_end;
            }

            // Add the remaining text to the string
            highlightedText += text.substring(current, text.length);

            return highlightedText;
        }
    }
};
</script>

<style scoped>
.card-text {
    max-height: 500px;
    overflow-y: auto;
}
.nps-metric{
    font-weight: 700 !important;
    font-size: 22px !important;
}
</style>
